import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle, AlertCircle, LogIn, LogOut, UserPlus } from 'lucide-react';
import { FaGoogle, FaLinkedin } from 'react-icons/fa';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { supabase } from '../lib/supabaseClient';
import { validateDocumentFile } from '../utils/extractDocumentText';
import { scanCvFileForSkills } from '../utils/cvSkillExtraction';
import CVSkillConfirmation from '../components/candidate/CVSkillConfirmation';

const PublicJobApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Custom questions state
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});

  // Auth state
  const [user, setUser] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loginConfirmPassword, setLoginConfirmPassword] = useState('');

  // Profile creation modal (after submission)
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profilePassword, setProfilePassword] = useState('');
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState(''); // Store email after submission
  const [profileError, setProfileError] = useState(null);
  const [profileCreated, setProfileCreated] = useState(false);

  const [formData, setFormData] = useState({
    // Candidate Table Fields (Personal Information)
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    location_city: '',
    location_state: '',
    location_country: 'Australia',
    clearance_level: '',
    citizenship_status: '',
    payroll_preference: '',
    pty_ltd_abn: '',
    linkedin_url: '',

    // Application Table Fields (Application-Specific)
    locations_available: [],
    asking_rate_value: '',
    asking_rate_basis: 'hour',
    notice_period: '',
    authority_to_represent: false,
    selection_criteria_response: '',
  });

  const [files, setFiles] = useState({
    resume: null,
    cover_letter: null,
  });

  // CV skill extraction (held in state only - written after the application is
  // submitted, once we have a candidate_id to attach them to)
  const [cvSkills, setCvSkills] = useState([]);
  const [cvSkillsLoading, setCvSkillsLoading] = useState(false);
  const [cvSkillsError, setCvSkillsError] = useState(null);
  const [cvSkillsExcluded, setCvSkillsExcluded] = useState(0);

  useEffect(() => {
    fetchRFQ();
    fetchCustomQuestions();
    checkAuthStatus();
  }, [id]);

  // Check if user is already logged in
  const checkAuthStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUser(user);
      // Reconcile any pending applications
      await reconcilePendingApplications(user);
      // Pre-fill form with candidate data
      await prefillFormData(user.email);
    }
  };

  // Reconcile pending applications when user logs in
  const reconcilePendingApplications = async (user) => {
    try {
      const { data, error } = await supabase.rpc('reconcile_pending_applications', {
        p_auth_user_id: user.id,
        p_email: user.email
      });

      if (error) {
        console.error('Error reconciling applications:', error);
        return;
      }

      if (data && data.reconciled_count > 0) {
        console.log(`Reconciled ${data.reconciled_count} pending application(s)`);
        // Optionally show a notification to the user
      }
    } catch (err) {
      console.error('Reconciliation error:', err);
    }
  };

  // Pre-fill form with existing candidate data
  const prefillFormData = async (email) => {
    try {
      const { data: candidate, error } = await supabase
        .from('candidates')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (error || !candidate) return;

      // Extract LinkedIn URL from social_links array
      const linkedInLink = candidate.social_links?.find(
        (link) => link.type === 'LinkedIn'
      );

      // Pre-fill the form with candidate data
      setFormData((prev) => ({
        ...prev,
        first_name: candidate.first_name || '',
        last_name: candidate.last_name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        location_city: candidate.location_city || '',
        location_state: candidate.location_state || '',
        location_country: candidate.location_country || 'Australia',
        clearance_level: candidate.clearance_level || '',
        citizenship_status: candidate.citizenship_status || '',
        payroll_preference: candidate.payroll_type || '',
        pty_ltd_abn: candidate.pty_ltd_abn || '',
        linkedin_url: linkedInLink?.url || '',
      }));
    } catch (err) {
      console.error('Error prefilling form:', err);
    }
  };

  const fetchRFQ = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rfqs')
        .select(`
          *,
          client:clients(name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data.display_status !== 'visible') {
        setError('This job listing is not accepting applications.');
        setRfq(null);
      } else {
        setRfq(data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching RFQ:', err);
      setError('Job listing not found.');
      setRfq(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('rfq_questions')
        .select(`
          id,
          client_question:client_questions(
            id,
            question_text,
            is_required,
            display_order
          )
        `)
        .eq('rfq_id', id)
        .eq('is_enabled', true)
        .order('client_question(display_order)', { ascending: true });

      if (error) throw error;

      setCustomQuestions(data || []);
    } catch (err) {
      console.error('Error fetching custom questions:', err);
      // Don't block the form if custom questions fail to load
      setCustomQuestions([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLocationToggle = (location) => {
    setFormData((prev) => ({
      ...prev,
      locations_available: prev.locations_available.includes(location)
        ? prev.locations_available.filter((loc) => loc !== location)
        : [...prev.locations_available, location],
    }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    // The accept attribute alone is a hint, not enforcement - validate for real
    const { valid, error: fileError } = validateDocumentFile(file);
    if (!valid) {
      setError(fileError);
      e.target.value = '';
      return;
    }

    setError(null);
    setFiles((prev) => ({
      ...prev,
      [fileType]: file,
    }));

    // Scan the resume for skills so the applicant can confirm them before submitting
    if (fileType === 'resume') {
      scanResumeForSkills(file);
    }
  };

  const scanResumeForSkills = async (file) => {
    setCvSkillsLoading(true);
    setCvSkillsError(null);
    setCvSkills([]);
    setCvSkillsExcluded(0);

    try {
      const { matched, excludedCount } = await scanCvFileForSkills(file, {
        context: 'public_application',
      });
      setCvSkills(matched);
      setCvSkillsExcluded(excludedCount);
    } catch (err) {
      // Never block the application on this - it is a convenience only
      console.error('CV skill scan failed:', err);
      setCvSkillsError(err.message || 'Could not read skills from your CV');
    } finally {
      setCvSkillsLoading(false);
    }
  };

  const handleCustomAnswerChange = (questionId, answer) => {
    setCustomAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    console.log('=== APPLICATION SUBMISSION STARTED ===');
    console.log('User logged in:', !!user);
    console.log('Form data:', formData);

    // Validate authority to represent checkbox
    if (!formData.authority_to_represent) {
      setError('You must confirm that you give Appsytec exclusive authority to represent you for this opportunity.');
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate asking rate (max value for database field precision 10, scale 2)
    if (formData.asking_rate_value) {
      const rateValue = parseFloat(formData.asking_rate_value);
      if (isNaN(rateValue) || rateValue < 0 || rateValue > 99999999.99) {
        setError('Please enter a valid asking rate');
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    try {
      // Get auth user ID if logged in
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const authUserId = authUser?.id || null;

      // Upload files to Supabase Storage if provided
      let resumeUrl = null;
      let coverLetterUrl = null;

      console.log('Step 1: Uploading files...');
      console.log('Resume file:', files.resume?.name);
      console.log('Cover letter file:', files.cover_letter?.name);

      if (files.resume) {
        const fileName = `${Date.now()}_${files.resume.name}`;
        console.log('Uploading resume to:', `applications/${fileName}`);
        const { error: uploadError } = await supabase.storage
          .from('candidate-documents')
          .upload(`applications/${fileName}`, files.resume);

        if (uploadError) {
          console.error('Resume upload error:', uploadError);
          throw new Error(`Resume upload failed: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage
          .from('candidate-documents')
          .getPublicUrl(`applications/${fileName}`);

        resumeUrl = urlData.publicUrl;
        console.log('Resume uploaded successfully:', resumeUrl);
      }

      if (files.cover_letter) {
        const fileName = `${Date.now()}_${files.cover_letter.name}`;
        console.log('Uploading cover letter to:', `applications/${fileName}`);
        const { error: uploadError } = await supabase.storage
          .from('candidate-documents')
          .upload(`applications/${fileName}`, files.cover_letter);

        if (uploadError) {
          console.error('Cover letter upload error:', uploadError);
          throw new Error(`Cover letter upload failed: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage
          .from('candidate-documents')
          .getPublicUrl(`applications/${fileName}`);

        coverLetterUrl = urlData.publicUrl;
        console.log('Cover letter uploaded successfully:', coverLetterUrl);
      }

      // Prepare additional_info with custom question answers
      const additionalInfo = customQuestions.map((q) => ({
        question_id: q.client_question.id,
        question_text: q.client_question.question_text,
        answer: customAnswers[q.client_question.id] || '',
      }));

      // Submit application using new scenario-based function
      console.log('\nStep 2: Submitting application with scenario detection...');
      console.log('Auth User ID:', authUserId);
      console.log('Email:', formData.email);

      const { data: result, error: submitError } = await supabase.rpc(
        'submit_application_with_verification',
        {
          p_auth_user_id: authUserId,
          p_rfq_id: id,
          p_email: formData.email,
          p_first_name: formData.first_name,
          p_last_name: formData.last_name,
          p_phone: formData.phone,
          p_location_city: formData.location_city,
          p_location_state: formData.location_state,
          p_location_country: formData.location_country || 'Australia',
          p_clearance_level: formData.clearance_level || null,
          p_citizenship_status: formData.citizenship_status || null,
          p_payroll_type: formData.payroll_preference || null,
          p_pty_ltd_abn: formData.pty_ltd_abn || null,
          // Normalize LinkedIn URL - add https:// if missing
          p_linkedin_url: formData.linkedin_url
            ? (formData.linkedin_url.startsWith('http://') || formData.linkedin_url.startsWith('https://')
              ? formData.linkedin_url
              : `https://${formData.linkedin_url}`)
            : null,
          p_locations_available: formData.locations_available.join(', '),
          p_asking_rate_value: formData.asking_rate_value ? parseFloat(formData.asking_rate_value) : null,
          p_asking_rate_basis: formData.asking_rate_basis,
          p_notice_period: formData.notice_period || null,
          p_authority_to_represent: formData.authority_to_represent,
          p_selection_criteria_response: formData.selection_criteria_response || null,
          p_cover_letter_url: coverLetterUrl,
          p_resume_url: resumeUrl,
          p_additional_info: additionalInfo.length > 0 ? additionalInfo : []
        }
      );

      if (submitError) {
        console.error('!!! APPLICATION SUBMISSION ERROR !!!');
        console.error('Error:', submitError);
        throw submitError;
      }

      // Check if the function returned an error (duplicate application)
      if (result?.error) {
        console.error('Application already exists:', result.message);
        throw new Error(result.message || 'You have already applied to this position');
      }

      console.log('Application submission result:', result);
      console.log('Scenario:', result.scenario);
      console.log('Status:', result.status);
      console.log('Needs verification:', result.needs_verification);

      // Store result metadata for displaying appropriate message
      sessionStorage.setItem('application_result', JSON.stringify(result));

      // Send verification emails for scenarios 2, 3, and 4
      if (result.needs_verification && result.email) {
        console.log('\nStep 2.5: Sending verification email...');
        console.log('Scenario:', result.scenario);

        try {
          // Determine redirect URL based on scenario
          const redirectTo = `${window.location.origin}/auth/callback`;

          if (result.scenario === 'new_candidate' ||
              result.scenario === 'existing_candidate_no_account') {
            // Scenarios 2 & 3: Use backend invite endpoint (creates user via admin API)
            console.log('Sending invite email for new user:', result.email);
            console.log('Redirect URL:', redirectTo);

            const apiUrl = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
            const response = await fetch(`${apiUrl}/auth/invite-applicant`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: result.email,
                redirectTo: redirectTo
              })
            });

            const inviteResult = await response.json();
            console.log('Invite response:', inviteResult);

            if (!response.ok || inviteResult.error) {
              console.error('Error sending invite:', inviteResult.error);
              // Store error info so we can show it to user on success page
              sessionStorage.setItem('magic_link_error', JSON.stringify({
                message: inviteResult.error || 'Failed to send invitation email',
                code: response.status
              }));
            } else {
              console.log('Invite sent successfully to:', result.email);
              sessionStorage.removeItem('magic_link_error');
            }

          } else if (result.scenario === 'existing_account_needs_login') {
            // Scenario 4: Magic link login only (account already exists)
            console.log('Sending magic link login for existing account:', result.email);

            const { data: otpData, error: otpError } = await supabase.auth.signInWithOtp({
              email: result.email,
              options: {
                emailRedirectTo: redirectTo,
                shouldCreateUser: false, // Do NOT create account - they already have one
                data: {
                  source: 'job_application',
                  application_id: result.application_id,
                  scenario: result.scenario
                }
              }
            });

            if (otpError) {
              console.error('Error sending magic link:', otpError);
              // Don't fail the whole submission if email fails
            } else {
              console.log('Magic link sent successfully to:', result.email);
            }
          }
        } catch (emailError) {
          console.error('Non-fatal error sending verification email:', emailError);
          // Continue with success flow even if email fails
        }
      }

      // Add uploaded files to candidate_files table (only if we have a candidate_id)
      if (result.candidate_id && (resumeUrl || coverLetterUrl)) {
        console.log('\nStep 3: Adding files to candidate_files...');
        const filesToInsert = [];

        if (resumeUrl && files.resume) {
          filesToInsert.push({
            file_name: files.resume.name,
            file_type: 'cv',
            file_size: files.resume.size,
            file_url: resumeUrl,
          });
        }

        if (coverLetterUrl && files.cover_letter) {
          filesToInsert.push({
            file_name: files.cover_letter.name,
            file_type: 'cover_letter',
            file_size: files.cover_letter.size,
            file_url: coverLetterUrl,
          });
        }

        if (filesToInsert.length > 0) {
          // Use RPC function to bypass RLS for public applications
          const { data: filesResult, error: filesError } = await supabase.rpc(
            'add_application_files',
            {
              p_candidate_id: result.candidate_id,
              p_application_id: result.application_id,
              p_files: filesToInsert,
            }
          );

          if (filesError) {
            console.error('Files insert error (non-fatal):', filesError);
          } else {
            console.log('Files added successfully:', filesResult);
          }
        }
      }

      // Attach the skills the applicant confirmed from their CV.
      // Sends skill IDs only - the RPC drops anything not active in skills_master,
      // so this cannot be used to add entries to the skills catalogue.
      if (result.candidate_id && cvSkills.length > 0) {
        console.log(`\nStep 4: Attaching ${cvSkills.length} confirmed CV skill(s)...`);
        const { error: skillsError } = await supabase.rpc('add_application_skills', {
          p_candidate_id: result.candidate_id,
          p_application_id: result.application_id,
          p_skill_ids: cvSkills.map((s) => s.skill_id),
        });

        if (skillsError) {
          // Non-fatal: the application itself is already saved
          console.error('Skills insert error (non-fatal):', skillsError);
        }
      }

      // Remember the email so the post-submission account offer can use it
      setCandidateEmail(result.email || formData.email);

      console.log('\n=== APPLICATION SUBMISSION COMPLETED ===');
      setSuccess(true);
    } catch (err) {
      console.error('\n!!! APPLICATION SUBMISSION FAILED !!!');
      console.error('Error:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
      console.log('=== APPLICATION SUBMISSION ENDED ===\n');
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) throw error;

      setUser(data.user);
      setShowLoginForm(false);
      setLoginEmail('');
      setLoginPassword('');

      // Reconcile any pending applications
      await reconcilePendingApplications(data.user);

      // Pre-fill form with their data
      await prefillFormData(data.user.email);
    } catch (err) {
      console.error('Login error:', err);
      setLoginError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoggingIn(false);
    }
  };

  // Handle email registration
  const handleRegister = async (e) => {
    e.preventDefault();
    if (loginPassword !== loginConfirmPassword) {
      setLoginError('Passwords do not match.');
      return;
    }
    // Matches the minimum enforced on the login page
    if (loginPassword.length < 8) {
      setLoginError('Password must be at least 8 characters.');
      return;
    }
    setLoggingIn(true);
    setLoginError(null);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: loginEmail,
        password: loginPassword,
      });
      if (error) throw error;
      if (data.user) {
        setUser(data.user);
        setShowLoginForm(false);
        setIsRegistering(false);
        setLoginEmail('');
        setLoginPassword('');
        setLoginConfirmPassword('');
        await prefillFormData(data.user.email);
      } else {
        // Email confirmation required
        setLoginError('Check your email to confirm your account, then log in.');
      }
    } catch (err) {
      console.error('Register error:', err);
      setLoginError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  // Handle OAuth login (Google / LinkedIn) — stores return URL so AuthCallback
  // redirects back here after auth completes.
  const handleOAuthLogin = async (provider) => {
    sessionStorage.setItem('oauth_return_url', `/job/${id}/apply`);
    const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;
    const options = { redirectTo: `${SITE_URL}/auth/callback` };
    if (provider === 'linkedin_oidc') options.scopes = 'openid profile email';
    const { error } = await supabase.auth.signInWithOAuth({ provider, options });
    if (error) setLoginError(`Sign-in failed: ${error.message}`);
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    // Clear form
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      location_city: '',
      location_state: '',
      location_country: 'Australia',
      clearance_level: '',
      citizenship_status: '',
      payroll_preference: '',
      pty_ltd_abn: '',
      linkedin_url: '',
      locations_available: [],
      asking_rate_value: '',
      asking_rate_basis: 'hour',
      notice_period: '',
      authority_to_represent: false,
      selection_criteria_response: '',
    });
  };

  // Handle profile creation with email + password (after submission)
  const handleCreateProfile = async () => {
    setProfileError(null);

    // Matches the minimum enforced on the login page
    if (!profilePassword || profilePassword.length < 8) {
      setProfileError('Password must be at least 8 characters');
      return;
    }

    setCreatingProfile(true);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: candidateEmail,
        password: profilePassword,
      });

      if (signUpError) throw signUpError;

      // The handle_new_user trigger links the candidates row created during this
      // application to the new auth user by email, so there is nothing to link here.
      if (data.user) {
        setUser(data.user);
      }

      setShowProfileModal(false);
      setProfilePassword('');
      setProfileCreated(true);
    } catch (err) {
      console.error('Error creating profile:', err);
      setProfileError(err.message || 'Failed to create profile. Please try again.');
    } finally {
      setCreatingProfile(false);
    }
  };

  // Handle profile creation via Google OAuth (after submission)
  const handleCreateProfileWithGoogle = async () => {
    setProfileError(null);
    setCreatingProfile(true);

    try {
      // Bring them back to their applications list once the OAuth round trip finishes
      sessionStorage.setItem('oauth_return_url', '/candidate/applications');

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (oauthError) throw oauthError;
      // On success the browser navigates away; nothing further to do here.
    } catch (err) {
      console.error('Error starting Google sign-up:', err);
      setProfileError(err.message || 'Could not start Google sign-up. Please try again.');
      setCreatingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading application form...</p>
        </div>
      </div>
    );
  }

  if (error && !rfq) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-card border-b shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <img
              src="/appsytec-logo.png"
              alt="Appsytec Logo"
              className="h-10 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-2">{error}</p>
              <Button onClick={() => navigate(`/job/${id}`)} variant="outline" className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Job Listing
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (success) {
    // Get submission result from sessionStorage
    const resultData = sessionStorage.getItem('application_result');
    const result = resultData ? JSON.parse(resultData) : null;
    const scenario = result?.scenario;
    const needsVerification = result?.needs_verification || false;

    // Check if magic link failed
    const magicLinkErrorData = sessionStorage.getItem('magic_link_error');
    const magicLinkError = magicLinkErrorData ? JSON.parse(magicLinkErrorData) : null;

    // Debug logging
    console.log('Success screen - result:', result);
    console.log('Success screen - scenario:', scenario);
    console.log('Success screen - magicLinkError:', magicLinkError);

    return (
      <div className="min-h-screen bg-background">
        <header className="bg-card border-b shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <img
              src="/appsytec-logo.png"
              alt="Appsytec Logo"
              className="h-10 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="pt-6 text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Application Submitted!</h2>

              {/* Scenario 1: Authenticated user OR no scenario (fallback) */}
              {(scenario === 'authenticated_user' || !scenario) && (
                <p className="text-muted-foreground mb-6">
                  Thank you for applying to {rfq?.title}. We'll review your application and get back to
                  you shortly.
                </p>
              )}

              {/* Scenarios 2 & 3: Unauthenticated submission - offer account creation */}
              {(scenario === 'new_candidate' || scenario === 'existing_candidate_no_account' || needsVerification) &&
               scenario !== 'authenticated_user' && scenario !== 'existing_account_needs_login' && (
                <div className="mb-6">
                  <p className="text-muted-foreground mb-4">
                    Thank you for applying to {rfq?.title}. Your application has been submitted and we'll review it shortly.
                  </p>
                  {profileCreated ? (
                    <Alert className="text-left mb-4 bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription>
                        <strong>Account created</strong>
                        <p className="mt-1">
                          You can now track this application and pre-fill future ones.
                        </p>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Card className="text-left mb-4 border-blue-200 bg-blue-50/50">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-1 flex items-center">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Create an account to track this application
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Optional: your application is already submitted and will be
                          reviewed either way. An account lets you track its status,
                          see all your applications in one place, and pre-fill future ones.
                        </p>

                        {profileError && (
                          <Alert className="mb-4 bg-destructive/10 border-destructive/30">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{profileError}</AlertDescription>
                          </Alert>
                        )}

                        <div className="flex flex-col gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCreateProfileWithGoogle}
                            disabled={creatingProfile}
                            className="w-full"
                          >
                            <FaGoogle className="h-4 w-4 mr-2" />
                            Continue with Google
                          </Button>

                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-blue-50 px-2 text-muted-foreground">or</span>
                            </div>
                          </div>

                          <Button
                            type="button"
                            onClick={() => {
                              setProfileError(null);
                              setShowProfileModal(true);
                            }}
                            disabled={creatingProfile}
                            className="w-full"
                          >
                            Set a password for {candidateEmail || result?.email}
                          </Button>
                        </div>

                        {magicLinkError && (
                          <p className="text-xs text-muted-foreground mt-4">
                            Note: we also tried emailing you a setup link but couldn't send it
                            ({magicLinkError.message}). You can create your account here instead.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Scenario 4: Existing account needs login */}
              {scenario === 'existing_account_needs_login' && (
                <div className="mb-6">
                  <p className="text-muted-foreground mb-4">
                    Your application for {rfq.title} has been saved in a pending state.
                  </p>
                  <Alert className="text-left mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>It looks like you already have an account</strong> with this email address.
                      Please check your email or log in to attach this application to your profile.
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={() => {
                      sessionStorage.setItem('prefill_email', result.email);
                      navigate('/login');
                    }}
                    className="mt-4"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Log In
                  </Button>
                </div>
              )}

              <div className="flex gap-4 justify-center mt-6">
                <Button onClick={() => navigate(`/job/${id}`)} variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Job Listing
                </Button>
                {user ? (
                  <Button onClick={() => navigate('/candidate')}>Return to Dashboard</Button>
                ) : (
                  <Button onClick={() => navigate('/')}>Return to Home</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <img
            src="/appsytec-logo.png"
            alt="Appsytec Logo"
            className="h-10 w-auto object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <Button variant="outline" onClick={() => navigate(`/job/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Job
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Login/Logout Section */}
        {!user && !showLoginForm && (
          <Card className="mb-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Speed up your application
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Log in or create an account to pre-fill your information
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => { setIsRegistering(false); setShowLoginForm(true); }} variant="outline">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                  <Button onClick={() => { setIsRegistering(true); setShowLoginForm(true); }} variant="default">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Register
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!user && showLoginForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{isRegistering ? 'Create an Account' : 'Login to Your Account'}</CardTitle>
              <CardDescription>
                {isRegistering
                  ? 'Create an account to track your applications and pre-fill future forms'
                  : 'Log in to pre-fill your information from previous applications'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* OAuth options */}
              <div className="space-y-2 mb-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleOAuthLogin('google')}
                >
                  <FaGoogle className="h-4 w-4 mr-2" />
                  Continue with Google
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleOAuthLogin('linkedin_oidc')}
                >
                  <FaLinkedin className="h-4 w-4 mr-2 text-[#0077B5]" />
                  Continue with LinkedIn
                </Button>
              </div>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    or {isRegistering ? 'register' : 'sign in'} with email
                  </span>
                </div>
              </div>

              <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
                {loginError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{loginError}</AlertDescription>
                  </Alert>
                )}
                <div>
                  <label className="text-sm font-medium mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Password</label>
                  <Input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
                {isRegistering && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">Confirm Password</label>
                    <Input
                      type="password"
                      value={loginConfirmPassword}
                      onChange={(e) => setLoginConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" disabled={loggingIn}>
                    {loggingIn ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {isRegistering ? 'Creating account...' : 'Logging in...'}
                      </>
                    ) : (
                      <>
                        <LogIn className="h-4 w-4 mr-2" />
                        {isRegistering ? 'Create Account' : 'Login'}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowLoginForm(false);
                      setIsRegistering(false);
                      setLoginError(null);
                      setLoginConfirmPassword('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  {isRegistering ? (
                    <>Already have an account?{' '}
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => { setIsRegistering(false); setLoginError(null); setLoginConfirmPassword(''); }}
                      >Sign in</button>
                    </>
                  ) : (
                    <>Don't have an account?{' '}
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => { setIsRegistering(true); setLoginError(null); }}
                      >Register</button>
                    </>
                  )}
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        {user && (
          <Card className="mb-6 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                    Logged in as {user.email}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Your information has been pre-filled
                  </p>
                </div>
                <Button onClick={handleLogout} variant="outline">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Apply for {rfq.title}</h1>
          <p className="text-muted-foreground">
            {rfq.client?.name} • {rfq.ref_code}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information - Candidate Table Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Tell us about yourself</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    First Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Last Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Email <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Phone <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+61 400 000 000"
                    required
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    City <span className="text-destructive">*</span>
                  </label>
                  <Input
                    name="location_city"
                    value={formData.location_city}
                    onChange={handleInputChange}
                    placeholder="Canberra"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    State/Territory <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="location_state"
                    value={formData.location_state}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select...</option>
                    <option value="ACT">ACT</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="SA">SA</option>
                    <option value="WA">WA</option>
                    <option value="TAS">TAS</option>
                    <option value="NT">NT</option>
                  </select>
                </div>
              </div>

              {/* Professional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Security Clearance Level
                  </label>
                  <select
                    name="clearance_level"
                    value={formData.clearance_level}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">None</option>
                    <option value="Baseline">Baseline</option>
                    <option value="NV1">NV1</option>
                    <option value="NV2">NV2</option>
                    <option value="TSPV">TSPV</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Citizenship Status
                  </label>
                  <select
                    name="citizenship_status"
                    value={formData.citizenship_status}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="Australian Citizen">Australian Citizen</option>
                    <option value="Permanent Resident">Permanent Resident</option>
                    <option value="Temporary Visa">Temporary Visa</option>
                  </select>
                </div>
              </div>

              {/* Payroll */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Preferred Payroll Type
                  </label>
                  <select
                    name="payroll_preference"
                    value={formData.payroll_preference}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="PAYG">PAYG</option>
                    <option value="ABN">ABN (Contractor)</option>
                  </select>
                </div>
                {formData.payroll_preference === 'ABN' && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      ABN Number
                    </label>
                    <Input
                      name="pty_ltd_abn"
                      value={formData.pty_ltd_abn}
                      onChange={handleInputChange}
                      placeholder="12 345 678 901"
                    />
                  </div>
                )}
              </div>

              {/* LinkedIn */}
              <div>
                <label className="text-sm font-medium mb-1 block">LinkedIn Profile URL</label>
                <Input
                  type="text"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  placeholder="linkedin.com/in/your-profile"
                />
              </div>
            </CardContent>
          </Card>

          {/* Application Details - Application Table Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Application Details</CardTitle>
              <CardDescription>Rate, availability, and location preferences for this role</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Asking Rate ($)
                  </label>
                  <Input
                    type="number"
                    name="asking_rate_value"
                    value={formData.asking_rate_value}
                    onChange={handleInputChange}
                    min="0"
                    max="99999999.99"
                    step="0.01"
                    placeholder="100.00"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Rate Basis</label>
                  <select
                    name="asking_rate_basis"
                    value={formData.asking_rate_basis}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="hour">Per Hour</option>
                    <option value="day">Per Day</option>
                    <option value="week">Per Week</option>
                    <option value="month">Per Month</option>
                    <option value="year">Per Year</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Notice Period / Availability
                </label>
                <select
                  name="notice_period"
                  value={formData.notice_period}
                  onChange={handleInputChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select...</option>
                  <option value="Immediate">Immediate / Available Now</option>
                  <option value="1 week">1 Week</option>
                  <option value="2 weeks">2 Weeks</option>
                  <option value="1 month">1 Month</option>
                  <option value="2 months">2 Months</option>
                  <option value="3 months">3 Months</option>
                  <option value="Other">Other (specify in cover letter)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Locations Available to Work (Select all that apply)</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['ACT', 'NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'Remote'].map((location) => (
                    <button
                      key={location}
                      type="button"
                      onClick={() => handleLocationToggle(location)}
                      className={`px-3 py-1 rounded-md text-sm border transition-colors ${formData.locations_available.includes(location)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-input hover:bg-accent'
                        }`}
                    >
                      {location}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="authority_to_represent"
                  name="authority_to_represent"
                  checked={formData.authority_to_represent}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  required
                />
                <label htmlFor="authority_to_represent" className="text-sm">
                  I confirm that I give Appsytec exclusive authority to represent me for this opportunity <span className="text-destructive">*</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Custom Application Questions */}
          {customQuestions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Questions</CardTitle>
                <CardDescription>
                  Please answer the following questions from {rfq.client?.name}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {customQuestions
                  .filter((question) => question.client_question != null)
                  .map((question) => (
                    <div key={question.client_question.id}>
                      <label className="text-sm font-medium mb-1 block">
                        {question.client_question.question_text}
                        {question.client_question.is_required && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </label>
                      <Textarea
                        value={customAnswers[question.client_question.id] || ''}
                        onChange={(e) =>
                          handleCustomAnswerChange(question.client_question.id, e.target.value)
                        }
                        rows={3}
                        placeholder="Your answer..."
                        required={question.client_question.is_required}
                        className="w-full"
                      />
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}

          {/* Selection Criteria Response - not required this way, get them to upload*/}
          {/* <Card>
            <CardHeader>
              <CardTitle>Selection Criteria Response</CardTitle>
              <CardDescription>Address the selection criteria for this role</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                name="selection_criteria_response"
                value={formData.selection_criteria_response}
                onChange={handleInputChange}
                rows={8}
                placeholder="Please address each of the selection criteria outlined in the job description..."
              />
            </CardContent>
          </Card> */}

          {/* Resume Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Resume <span className="text-destructive">*</span></CardTitle>
              <CardDescription>Upload your resume (PDF, DOC, DOCX)</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileChange(e, 'resume')}
                required
              />
              {files.resume && (
                <p className="text-xs text-muted-foreground mt-2">
                  Selected: {files.resume.name}
                </p>
              )}

              {(cvSkillsLoading || cvSkills.length > 0 || cvSkillsError || cvSkillsExcluded > 0) && (
                <div className="mt-4">
                  <CVSkillConfirmation
                    variant="inline"
                    skills={cvSkills}
                    loading={cvSkillsLoading}
                    error={cvSkillsError}
                    excludedCount={cvSkillsExcluded}
                    onChange={setCvSkills}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cover Letter Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Selection Criteria</CardTitle>
              <CardDescription>Upload your Selection Criteria response (PDF, DOC, DOCX)</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileChange(e, 'cover_letter')}
              />
              {files.cover_letter && (
                <p className="text-xs text-muted-foreground mt-2">
                  Selected: {files.cover_letter.name}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/job/${id}`)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Profile Creation Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <UserPlus className="h-5 w-5 mr-2" />
              Create Your Profile
            </DialogTitle>
            <DialogDescription>
              Would you like to create a profile to track your applications and save your information
              for future applications?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Creating a profile allows you to:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-2">
              <li>Track all your job applications in one place</li>
              <li>Pre-fill your information on future applications</li>
              <li>Get updates on your application status</li>
            </ul>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Set a Password <span className="text-destructive">*</span>
              </label>
              <Input
                type="password"
                value={profilePassword}
                onChange={(e) => setProfilePassword(e.target.value)}
                placeholder="At least 8 characters"
                minLength={8}
              />
              <p className="text-xs text-muted-foreground mt-1">
                You'll use {candidateEmail} and this password to log in
              </p>
            </div>
            {profileError && (
              <Alert className="bg-destructive/10 border-destructive/30">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{profileError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowProfileModal(false);
                setProfilePassword('');
              }}
            >
              Skip
            </Button>
            <Button onClick={handleCreateProfile} disabled={creatingProfile}>
              {creatingProfile ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Profile
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicJobApplication;
